import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';

/**
 * One dish logged on a visit (#110). Encapsulates the menu-item / off-menu
 * picker (with its own debounced catalog typeahead) plus a compact per-dish
 * rating — the food itself, distinct from the visit's overall experience rating.
 *
 * A `fixed` row (the Food-detail page's own dish) locks the picker to that dish
 * and can't be removed; the user rates it and may add more dishes around it.
 *
 * Controlled: the parent owns the dish object and merges `onChange` patches.
 * `dish` shape: { menuItemId, dishNameText, matchedFood, rating }.
 */
function VisitDishRow({ index, dish, menuItems = [], apiBaseUrl, fixed = false, fixedLabel = '', onChange, onRemove, onAddPhotos, onRemovePhoto, photoRoom = 0 }) {
  const [suggestions, setSuggestions] = useState([]);
  const photos = dish.photos || [];

  const isOther = dish.menuItemId === '__other__';
  const menuItemName = (mi) =>
    (mi.custom_name && mi.custom_name.trim()) ? mi.custom_name : (mi.food?.name || 'Dish');

  // Debounced catalog typeahead while naming an off-menu dish — same endpoint as
  // the single-dish path used (#102 rung-2). Stops once a match is locked in.
  useEffect(() => {
    if (fixed || !isOther || dish.matchedFood) { setSuggestions([]); return; }
    const term = (dish.dishNameText || '').trim();
    if (term.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/foods/search/${encodeURIComponent(term)}`);
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setSuggestions(Array.isArray(data) ? data.slice(0, 6) : []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [fixed, isOther, dish.dishNameText, dish.matchedFood, apiBaseUrl]);

  const rating = dish.rating || 0;

  const stars = (
    <div className="d-flex align-items-center gap-1 mt-2">
      <span className="text-muted small me-1">Rate this dish:</span>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          role="button"
          aria-label={`${star} star${star !== 1 ? 's' : ''}`}
          onClick={() => onChange({ rating: star })}
          style={{
            cursor: 'pointer',
            fontSize: '1.15rem',
            lineHeight: 1,
            color: star <= rating ? '#ffc107' : '#e4e5e9',
          }}
        >
          ★
        </span>
      ))}
      {rating > 0 && (
        <button
          type="button"
          className="btn btn-link btn-sm text-muted p-0 ms-1"
          style={{ fontSize: '0.75rem', textDecoration: 'none' }}
          onClick={() => onChange({ rating: 0 })}
        >
          clear
        </button>
      )}
    </div>
  );

  return (
    <div className="border rounded p-2 mb-2 dynerz-visit-dish-row">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="text-muted small fw-semibold">Dish {index + 1}</span>
        {!fixed && onRemove && (
          <button
            type="button"
            className="btn-close btn-close-sm"
            aria-label={`Remove dish ${index + 1}`}
            style={{ fontSize: '0.6rem' }}
            onClick={onRemove}
          />
        )}
      </div>

      {fixed ? (
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-light text-dark border">{fixedLabel || 'This dish'}</span>
        </div>
      ) : (
        <>
          <Form.Select
            size="sm"
            value={dish.menuItemId}
            onChange={(e) => onChange({ menuItemId: e.target.value, matchedFood: null, dishNameText: '' })}
          >
            <option value="">Not specified</option>
            {menuItems.map((mi) => (
              <option key={mi.id} value={mi.id}>{menuItemName(mi)}</option>
            ))}
            <option value="__other__">Something else (not on the menu)…</option>
          </Form.Select>

          {isOther && (
            <>
              {/* #119: the name you typed is the DISPLAY name and always stays
                  editable. Picking a catalog match links it to a known catalog
                  dish (#127: NOT a "category" — that's a separate taxonomy),
                  shown as a clearable badge, without discarding your wording. */}
              <Form.Control
                className="mt-2"
                size="sm"
                type="text"
                maxLength={150}
                placeholder="Name the dish you had"
                value={dish.dishNameText || ''}
                onChange={(e) => onChange({ dishNameText: e.target.value })}
              />
              {dish.matchedFood ? (
                <div className="mt-1 d-flex align-items-center gap-2">
                  <span className="text-muted small">Catalog dish:</span>
                  <span className="badge bg-light text-dark border d-inline-flex align-items-center gap-1">
                    {dish.matchedFood.name}
                    <button
                      type="button"
                      className="btn-close btn-close-sm"
                      aria-label="Clear catalog dish link"
                      style={{ fontSize: '0.6rem' }}
                      onClick={() => onChange({ matchedFood: null })}
                    />
                  </span>
                </div>
              ) : (
                <>
                  {suggestions.length > 0 && (
                    <div className="list-group mt-1">
                      {suggestions.map((food) => (
                        <button
                          type="button"
                          key={food.id}
                          className="list-group-item list-group-item-action py-1 d-flex justify-content-between align-items-center"
                          onClick={() => {
                            // #119: keep the typed name only when it's a genuine
                            // custom name — i.e. a REVERSE match where the user
                            // typed something more specific than the catalog
                            // dish ("Chocolate Chip Muffin" → "Muffin"). When the
                            // typed text is just a forward-search fragment OF the
                            // catalog name ("spic" → "Spicy Chicken Bowl"), adopt
                            // the full catalog name so the review shows that, not
                            // the fragment.
                            const typed = (dish.dishNameText || '').trim();
                            const wasSearchFragment =
                              typed.length > 0 && food.name.toLowerCase().includes(typed.toLowerCase());
                            onChange({
                              matchedFood: { id: food.id, name: food.name },
                              ...(wasSearchFragment ? { dishNameText: food.name } : {}),
                            });
                            setSuggestions([]);
                          }}
                        >
                          <span>{food.name}</span>
                          <small className="text-muted">catalog dish</small>
                        </button>
                      ))}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Optionally link a known dish from the catalog — you keep the name you typed.
                  </Form.Text>
                </>
              )}
            </>
          )}
        </>
      )}

      {stars}

      {/* #115: photos OF THIS DISH. Attributing a photo to the dish is what lets
          it show on the dish's public review — un-attributed shots stay private. */}
      {onAddPhotos && (
        <div className="mt-2">
          {photos.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-1">
              {photos.map((p, i) => (
                <div key={i} className="position-relative">
                  <img
                    src={p.previewUrl}
                    alt={`Dish ${index + 1} upload ${i + 1}`}
                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6 }}
                  />
                  <button
                    type="button"
                    className="btn-close btn-close-sm position-absolute top-0 end-0 bg-light rounded-circle"
                    aria-label={`Remove dish photo ${i + 1}`}
                    style={{ fontSize: '0.5rem', transform: 'translate(30%,-30%)' }}
                    onClick={() => onRemovePhoto(i)}
                  />
                </div>
              ))}
            </div>
          )}
          {photoRoom > 0 && (
            <label className="btn btn-outline-secondary btn-sm mb-0" style={{ fontSize: '0.75rem' }}>
              📷 Add photo of this dish
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => { onAddPhotos(Array.from(e.target.files || [])); e.target.value = ''; }}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

export default VisitDishRow;
